import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../axios/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../assets/NewFiles/Css/ForgetPasswordPage.css';
import { Mail, Smartphone, KeyRound, Lock, Eye, EyeOff, CheckCircle2, ArrowRight, MessageSquare } from 'lucide-react';

const ForgetPasswordPage = () => {
  const [resetMethod, setResetMethod] = useState('phone'); // 'phone' or 'email'
  const [step, setStep] = useState(1); // Step 1: Send OTP, Step 2: Verify & Reset, Step 3: Success
  const [targetInput, setTargetInput] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!targetInput.trim()) {
      toast.error(resetMethod === 'phone' ? 'Please enter mobile number' : 'Please enter email address');
      return;
    }

    if (resetMethod === 'phone') {
      const cleanPhone = targetInput.replace(/[^0-9]/g, '');
      if (cleanPhone.length !== 10) {
        toast.error('Please enter a valid 10-digit mobile number');
        return;
      }
    }

    setIsLoading(true);
    try {
      const payload = resetMethod === 'phone'
        ? { phoneNumber: targetInput.replace(/[^0-9]/g, '') }
        : { userEmail: targetInput.toLowerCase().trim() };

      const response = await api.post('/api/auth/sendotp', payload);
      toast.success(response.data.message || 'OTP code sent successfully!');
      setStep(2);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to send OTP. Please check your details.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!otp.trim() || !newPassword.trim()) {
      toast.error('OTP code and New Password are required');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      const payload = resetMethod === 'phone'
        ? { phoneNumber: targetInput.replace(/[^0-9]/g, ''), otp: otp.trim(), newPassword }
        : { userEmail: targetInput.toLowerCase().trim(), otp: otp.trim(), newPassword };

      const response = await api.post('/api/auth/verifyotp', payload);
      if (response.status === 200 || response.data.message === 'Password reset successfully') {
        toast.success('Password reset successfully!');
        setStep(3);
      } else {
        toast.error(response.data.message || 'Password reset failed');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Error resetting password. Please check your OTP.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forget-password-container">
      <div className="forget-password-card">
        <div className="forget-header">
          <h2>Reset Password</h2>
          <p>
            {step === 1 && 'Choose your recovery method to receive an OTP code'}
            {step === 2 && 'Enter the OTP code received and set your new password'}
            {step === 3 && 'Your password has been successfully updated'}
          </p>
        </div>

        {step === 1 && (
          <>
            <div className="method-selector">
              <button
                type="button"
                className={`method-btn ${resetMethod === 'phone' ? 'active' : ''}`}
                onClick={() => { setResetMethod('phone'); setTargetInput(''); }}
              >
                <MessageSquare size={16} /> SMS OTP
              </button>
              <button
                type="button"
                className={`method-btn ${resetMethod === 'email' ? 'active' : ''}`}
                onClick={() => { setResetMethod('email'); setTargetInput(''); }}
              >
                <Mail size={16} /> Email Address
              </button>
            </div>

            <form onSubmit={handleSendOtp}>
              {resetMethod === 'phone' ? (
                <div className="forget-form-group">
                  <label>Mobile Number <span style={{ color: '#ef4444' }}>*</span></label>
                  <div className="input-wrapper">
                    <Smartphone className="input-icon" size={18} />
                    <span className="phone-prefix">+91</span>
                    <input
                      type="tel"
                      maxLength={10}
                      className="with-prefix"
                      value={targetInput}
                      onChange={(e) => setTargetInput(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="9876543210"
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="forget-form-group">
                  <label>Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                  <div className="input-wrapper">
                    <Mail className="input-icon" size={18} />
                    <input
                      type="email"
                      value={targetInput}
                      onChange={(e) => setTargetInput(e.target.value)}
                      placeholder="name@company.com"
                      required
                    />
                  </div>
                </div>
              )}

              <button type="submit" className="forget-submit-btn" disabled={isLoading}>
                {isLoading ? 'Dispatching OTP...' : 'Send OTP Code'}
              </button>
            </form>
          </>
        )}

        {step === 2 && (
          <form onSubmit={handleResetPassword}>
            <div className="forget-form-group">
              <label>Verification OTP Code <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <KeyRound className="input-icon" size={18} />
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.trim())}
                  placeholder="Enter 6-digit OTP"
                  required
                />
              </div>
            </div>

            <div className="forget-form-group">
              <label>New Password <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" className="forget-submit-btn" disabled={isLoading}>
              {isLoading ? 'Updating Password...' : 'Reset Password'}
            </button>
          </form>
        )}

        {step === 3 && (
          <div>
            <div className="success-box">
              <CheckCircle2 size={40} style={{ color: '#166534', margin: '0 auto' }} />
              <h4>Password Changed!</h4>
              <p>You can now log in using your new credentials.</p>
            </div>
            <button
              type="button"
              className="forget-submit-btn"
              onClick={() => navigate('/login')}
            >
              Proceed to Sign In <ArrowRight size={18} />
            </button>
          </div>
        )}

        <div className="forget-footer-links">
          <Link to="/login" className="auth-link">Back to Sign In</Link>
          <span>•</span>
          <Link to="/signup" className="auth-link">Create Account</Link>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default ForgetPasswordPage;
